import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "ml-cookie-consent-v1";

export const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if user has not acknowledged yet, and only after age gate is confirmed
    const isPanel = window.location.pathname.startsWith("/painel") || window.location.pathname.startsWith("/admin");
    if (isPanel) return undefined;

    const check = () => {
      const consent = localStorage.getItem(STORAGE_KEY);
      const ageOk = localStorage.getItem("ml-age-ok") === "1";
      if (!consent && ageOk) {
        setVisible(true);
        return true;
      }
      return false;
    };

    // Poll until age gate is accepted (same-tab localStorage change doesn't fire "storage" event)
    if (check()) return undefined;
    const interval = setInterval(() => {
      if (check()) clearInterval(interval);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: "accepted", at: Date.now() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookies.title")}
      className="fixed inset-x-3 bottom-3 z-[70] sm:inset-x-auto sm:bottom-5 sm:right-5 sm:max-w-md"
      data-testid="cookie-banner"
    >
      <div className="rounded-2xl border border-gold/25 bg-background/95 p-5 shadow-2xl backdrop-blur-md">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold/12 text-gold">
            <Cookie className="h-4 w-4" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg text-foreground">{t("cookies.title")}</h3>
            <p className="mt-1 text-sm leading-relaxed text-foreground/70">
              {t("cookies.body")}{" "}
              <Link
                to="/cookies"
                className="text-gold underline underline-offset-4 transition-colors hover:text-gold-light"
              >
                {t("cookies.readMore")}
              </Link>
              .
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={accept}
                data-testid="cookie-accept"
                className="rounded-full bg-gold px-5 py-2 text-xs font-medium uppercase tracking-widest text-background transition-colors hover:bg-gold-light"
              >
                {t("cookies.accept")}
              </button>
              <Link
                to="/cookies"
                className="rounded-full border border-foreground/15 px-5 py-2 text-xs font-medium uppercase tracking-widest text-foreground/80 transition-colors hover:border-gold hover:text-gold"
              >
                {t("cookies.settings")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
